"use client";

import { login, loginWithGoogle } from "@/app/actions/auth";
import { logoCompleto } from "@kaizen/brand";
import { Loader2 } from "lucide-react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { Suspense, useActionState, useEffect, useRef, useState } from "react";

const PENDING_EMAIL_KEY = "kaizen.pendingEmail";

// Códigos de error que Auth.js v5 emite vía `?error=...` cuando redirige a
// `pages.error`. Cubrimos los documentados; cualquier otro cae en el caso
// genérico ("Default"). Los textos son intencionalmente neutros para no
// distinguir entre "no existe el usuario" y "no es superadmin" — ver decisión
// 3.6 anti-enumeración del informe 09 y H4 del informe 10.
//
// Ref: node_modules/@auth/core/errors.js (clientErrors set).
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  Verification:
    "El código no es válido o ha caducado. Vuelve a intentarlo o pide uno nuevo.",
  AccessDenied:
    "No tienes acceso a este panel o has superado el número de intentos. Espera unos minutos e inténtalo de nuevo.",
  OAuthSignin:
    "Hubo un problema al iniciar sesión con Google. Inténtalo de nuevo.",
  OAuthCallback:
    "Google no pudo completar el login. Inténtalo de nuevo o usa el código por email.",
  OAuthCreateAccount:
    "No pudimos crear la cuenta tras el login con Google. Inténtalo de nuevo.",
  OAuthAccountNotLinked:
    "Este email ya está vinculado a otra forma de acceso. Usa el método con el que te registraste.",
  EmailCreateAccount:
    "No pudimos crear la cuenta para este email. Inténtalo de nuevo.",
  EmailSignin:
    "No pudimos enviar el código por email. Inténtalo de nuevo en unos minutos.",
  CredentialsSignin: "Las credenciales no son válidas. Inténtalo de nuevo.",
  Callback: "Hubo un problema al completar el login. Inténtalo de nuevo.",
  SessionRequired: "Necesitas iniciar sesión para acceder a esta página.",
  Configuration:
    "Hay un problema de configuración en el servidor. Avisa al administrador.",
  Default:
    "Hubo un problema al iniciar sesión. Inténtalo de nuevo en unos minutos.",
};

function authErrorMessage(code: string | null): string | undefined {
  if (code === null) return undefined;
  return AUTH_ERROR_MESSAGES[code] ?? AUTH_ERROR_MESSAGES.Default;
}

export default function LoginPage() {
  // `useSearchParams` exige Suspense en prerender (Next.js 15 CSR bailout).
  // El contenido real vive en `LoginPageInner`.
  return (
    <Suspense fallback={null}>
      <LoginPageInner />
    </Suspense>
  );
}

function LoginPageInner() {
  const [loginState, loginAction, loginPending] = useActionState(
    login,
    undefined,
  );
  const searchParams = useSearchParams();
  const authError = searchParams.get("error");

  // El email se pierde en el round-trip al callback de Auth.js cuando hay error
  // (Auth.js redirige a `pages.error` sin propagar query/state). Lo persistimos
  // en sessionStorage para que la pantalla de código sobreviva a reintentos.
  const [pendingEmail, setPendingEmail] = useState("");
  useEffect(() => {
    if (loginState?.status === "sent" && loginState.email) {
      sessionStorage.setItem(PENDING_EMAIL_KEY, loginState.email);
      setPendingEmail(loginState.email);
    } else {
      setPendingEmail(sessionStorage.getItem(PENDING_EMAIL_KEY) ?? "");
    }
  }, [loginState]);

  const emailForCode = loginState?.email ?? pendingEmail;
  const showCodeScreen =
    loginState?.status === "sent" ||
    (authError !== null && emailForCode !== "");

  const resetPendingEmail = () => {
    sessionStorage.removeItem(PENDING_EMAIL_KEY);
    setPendingEmail("");
  };

  return (
    <div className="min-h-screen bg-surface text-text-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Image
            src={logoCompleto}
            alt="Kaizen Feedback"
            width={200}
            height={56}
            className="mx-auto h-12 w-auto"
            priority
          />
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-olive/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-olive-dark ring-1 ring-inset ring-olive/20">
            <span className="h-1.5 w-1.5 rounded-full bg-olive" aria-hidden />
            SuperAdmin
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-surface-elevated p-8 shadow-sm space-y-6">
          {showCodeScreen ? (
            <CodeScreen
              email={emailForCode}
              sentMessage={loginState?.message}
              errorMessage={authErrorMessage(authError)}
              onBack={resetPendingEmail}
            />
          ) : (
            <EmailScreen
              action={loginAction}
              pending={loginPending}
              errorMessage={
                loginState?.status === "error"
                  ? loginState.message
                  : authError === "Verification"
                    ? "El código que introdujiste no es válido o ha caducado. Empieza de nuevo: vuelve a pedir un código."
                    : authErrorMessage(authError)
              }
            />
          )}
        </div>

        <p className="text-center text-text-muted text-xs mt-8">
          &copy; {new Date().getFullYear()} Kaizen Feedback. Acceso restringido.
        </p>
      </div>
    </div>
  );
}

function EmailScreen({
  action,
  pending,
  errorMessage,
}: {
  action: (formData: FormData) => void;
  pending: boolean;
  errorMessage?: string;
}) {
  return (
    <>
      <form action={loginWithGoogle}>
        <button
          type="submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-surface-elevated px-4 py-3 text-sm font-medium text-text-primary hover:bg-surface transition-colors"
        >
          <GoogleIcon />
          Continuar con Google
        </button>
      </form>

      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-widest text-text-muted">
          o
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <form action={action} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-text-primary mb-1.5"
          >
            Recibir un código por email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="w-full rounded-lg border border-border bg-surface-elevated px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-olive focus:outline-none focus:ring-2 focus:ring-olive/30"
            placeholder="tu@email.com"
          />
        </div>

        {errorMessage && (
          <div className="rounded-lg border border-danger/30 bg-danger-bg p-3 text-sm text-danger">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={pending}
          className="w-full inline-flex items-center justify-center rounded-lg bg-olive px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-olive-dark disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {pending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            "Enviarme un código"
          )}
        </button>
      </form>
    </>
  );
}

function CodeScreen({
  email,
  sentMessage,
  errorMessage,
  onBack,
}: {
  email: string;
  sentMessage?: string;
  errorMessage?: string;
  onBack: () => void;
}) {
  // Form HTML plano con GET directo al callback de Auth.js. Evita server actions
  // porque la redirección desde un server action a una API route de Auth.js no
  // propaga correctamente las cookies de sesión en el navegador.
  const formRef = useRef<HTMLFormElement>(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset cuando llega un error nuevo (no nos quedamos en estado submitting).
  useEffect(() => {
    if (errorMessage) {
      setCode("");
      setSubmitting(false);
    }
  }, [errorMessage]);

  // Auto-submit al alcanzar 6 dígitos.
  useEffect(() => {
    if (code.length === 6 && /^\d{6}$/.test(code) && !submitting) {
      setSubmitting(true);
      formRef.current?.submit();
    }
  }, [code, submitting]);

  return (
    <form
      ref={formRef}
      method="GET"
      action="/api/auth/callback/email"
      className="space-y-4"
    >
      <div>
        <p className="text-sm text-text-primary mb-2">
          {sentMessage ??
            "Si tu email está autorizado, recibirás un código en breve."}
        </p>
        {email && (
          <p className="text-xs text-text-secondary mb-4">
            Lo enviamos a{" "}
            <span className="text-text-primary font-medium">{email}</span>.
            Introdúcelo aquí (también puedes clicar el enlace del email si lo
            abres en este mismo dispositivo).
          </p>
        )}

        <input type="hidden" name="email" value={email} />
        <input type="hidden" name="callbackUrl" value="/" />

        <label
          htmlFor="token"
          className="block text-sm font-medium text-text-primary mb-1.5"
        >
          Código de 6 dígitos
        </label>
        <input
          id="token"
          name="token"
          type="text"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => {
            // Quitar todo lo que no sea dígito (paste de "123 456" o similar).
            const cleaned = e.target.value.replace(/\D/g, "").slice(0, 6);
            setCode(cleaned);
          }}
          disabled={submitting}
          className="w-full rounded-lg border border-border bg-surface-elevated px-4 py-3 text-center text-2xl font-mono tracking-[0.6em] text-text-primary placeholder:text-text-muted focus:border-olive focus:outline-none focus:ring-2 focus:ring-olive/30 disabled:opacity-60"
          placeholder="••••••"
        />
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-danger/30 bg-danger-bg p-3 text-sm text-danger">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || code.length !== 6}
        className="w-full inline-flex items-center justify-center rounded-lg bg-olive px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-olive-dark disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          "Entrar al panel"
        )}
      </button>

      <p className="text-center text-xs text-text-muted">
        ¿Email equivocado?{" "}
        <a
          href="/login"
          onClick={onBack}
          className="font-medium text-olive hover:text-olive-dark hover:underline"
        >
          Volver
        </a>
      </p>
    </form>
  );
}

function GoogleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 48 48"
      aria-hidden="true"
    >
      <title>Google</title>
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}
